import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import theme from '@monorepo/shared/src/themes/defaultTheme';
import { DndContextWrapper } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/DragAndDrop';
import GWDragAndDrop from 'assets/images/drag-and-drop.png';
import {
  DndDragBox,
  DndDropBox,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/DragAndDrop';
import { ActivityContext } from 'state/providers/ActivityProvider';
import ActivityPlayButtonOverlay from '../../components/ActivityPlayButtonOverlay';
import useActivityAudio from '@monorepo/shared/src/hooks/use-activity-audio';
import GWhoRoundLayout from './GWhoRoundLayout';
import type { GuessWhoImage } from '@monorepo/shared/src/graph/schema-generated-types-student';
import { DndDragEndEvent, GuessStates } from 'types';
import clsx from 'clsx';
import AspectRatioWrapper from '@monorepo/shared/src/components/common/AspectRatioWrapper';
import { InstructionButtonContext } from 'state/providers/InstructionsButtonProvider';
import { getCorrectGuessStyle } from '../../util';

const t = (key: string, options?: Record<string, any>) => translate('activities', key, options);
interface Props {
  onRoundSuccess: () => void;
}

const GWhoDndRound: React.FC<Props> = ({ onRoundSuccess }) => {
  const [
    { sounds, isPlaying, currentSound },
    { playSoundFromState, playMultipleSounds, stopSound },
  ] = useActivityAudio();
  const [state, { setIsActivityOver, setShowNextButton }] = useContext(ActivityContext);

  const [correctGuesses, setCorrectGuesses] = useState<Array<string>>([]);
  const [guess, setGuess] = useState(GuessStates.NULL);
  const [guessIndicator, setGuessIndicator] = useState(GuessStates.NULL);
  const [focusedTileId, setFocusedTileId] = useState<string>('');
  const [showActivityPlayButton, setShowActivityPlayButton] = useState<boolean>(true);
  const [{ playInstructionFlag, instructionButtonDisabled }, { stopInstructions, setDisabled }] =
    useContext(InstructionButtonContext);

  const classes = styles();

  const currentRound = state?.currentRound?.data;

  useEffect(() => {
    setCorrectGuesses([]);
    if (!isPlaying && !showActivityPlayButton) playCurrentRoundQuestion();
  }, [currentRound.question]);

  useEffect(() => {
    const foundTile =
      currentSound && currentRound.tiles?.find((t) => t.audio?.src === currentSound?._src);
    foundTile && setFocusedTileId(foundTile.audio?.id);
  }, [currentSound]);

  const playAudioAndHighlightTiles = async () => {
    await playMultipleSounds(
      1000,
      currentRound.tiles.map(({ audio }) => audio && audio.description).filter((x) => x)
    );
    setFocusedTileId('');
  };

  const playCurrentRoundQuestion = async () => {
    currentRound?.question &&
      (await playSoundFromState(sounds[currentRound.question.description], true));

    currentRound?.tiles && (await playAudioAndHighlightTiles());
    stopInstructions();
  };

  useEffect(() => {
    if (playInstructionFlag && !instructionButtonDisabled) {
      stopSound();
      playCurrentRoundQuestion();
    }
    return () => {
      setDisabled(false);
    };
  }, [playInstructionFlag]);

  const playSummary = async () => {
    const summarySound = sounds[state?.activity?.summary?.description];
    summarySound && (await playSoundFromState(summarySound, true));
  };

  const playAffirmation = async () => {
    if (currentSound) stopSound();
    await playMultipleSounds(
      0,
      [currentRound?.affirmation?.description, currentRound?.answer?.description].filter((x) => x)
    );
  };

  const maybeEndActivity = async () => {
    if (state.isLastRound) {
      await setIsActivityOver(true);
      await playSummary();
      setShowNextButton(true);
      return;
    }
    setGuess(GuessStates.NULL);
  };

  const handleOnDragEnd = (event: DndDragEndEvent) => {
    setFocusedTileId('');

    if (event.over && event.active.data?.current.correct) {
      onTileDropSuccess(event.active.data?.current);
    }

    if (event.over && !event.active.data?.current.correct) {
      onTileDropError();
    }
  };

  const onActivityButtonClick = () => {
    setShowActivityPlayButton(false);
    playCurrentRoundQuestion();
  };

  const onTileDropSuccess = async (image: GuessWhoImage) => {
    setDisabled(true);
    setCorrectGuesses((currentState) => [image.description, ...currentState]);
    setGuessIndicator(GuessStates.SUCCESS);
    setGuess(GuessStates.SUCCESS);
    setTimeout(() => {
      setGuessIndicator(GuessStates.NULL);
    }, 500);

    await playAffirmation();

    setTimeout(() => {
      onRoundSuccess();
      maybeEndActivity();
    }, 2000);
    setDisabled(false);
  };

  const onTileDropError = async () => {
    playSoundFromState(sounds['boink'], true);
    setGuessIndicator(GuessStates.ERROR);
    if (!state.isLastRound) setGuess(GuessStates.ERROR);

    setTimeout(() => {
      setGuessIndicator(GuessStates.NULL);
      if (!state.isLastRound) setGuess(GuessStates.NULL);
    }, 500);
  };

  const dndBoxStyles = { width: '100%', height: '100%' };
  const SceneComponent = useCallback(
    () => (
      <AspectRatioWrapper ratio={760 / 810}>
        <DndDropBox styles={{ container: dndBoxStyles, refDiv: dndBoxStyles }}>
          <Paper
            className={clsx(classes.dndDropBox, guess === GuessStates.SUCCESS && classes.invisible)}
            elevation={4}
          >
            {guess === GuessStates.SUCCESS ? (
              <div className={classes.sceneContainer}>
                <img
                  alt={t('gwSceneImgAlt', {
                    alt: state?.currentRound?.data?.scene?.description,
                  })}
                  src={state?.currentRound?.data?.scene?.src}
                />
              </div>
            ) : (
              <img
                alt={t('gwDragAndDrop')}
                className={classes.dragAndDropImg}
                src={GWDragAndDrop}
              />
            )}
          </Paper>
        </DndDropBox>
      </AspectRatioWrapper>
    ),
    [guess, state?.currentRound?.round]
  );

  const tileComponents = useMemo(
    () =>
      (state?.currentRound?.data?.tiles || []).map((tile) => () => (
        <DndDragBox
          data={tile.image}
          key={tile.image.description}
          id={tile.image.description}
          isCorrectStyle={getCorrectGuessStyle(correctGuesses, tile.image.description)}
        >
          <Paper
            className={classes.tileBackground}
            elevation={4}
            style={{
              border:
                focusedTileId === tile.audio?.id ? `solid ${theme.palette.grey[900]} 3px` : 'none',
            }}
            id={tile.audio?.id || 'tile'}
          >
            <img src={tile.image.src} alt={tile.audio?.description} />
          </Paper>
        </DndDragBox>
      )),
    [state?.currentRound?.round, correctGuesses.length, focusedTileId]
  );

  return (
    <DndContextWrapper handleOnDragEnd={handleOnDragEnd}>
      <GWhoRoundLayout
        guessIndicator={guessIndicator}
        SceneComponent={<SceneComponent />}
        tileComponents={tileComponents}
      />
      {showActivityPlayButton && (
        <ActivityPlayButtonOverlay onOverlayClick={onActivityButtonClick} />
      )}
    </DndContextWrapper>
  );
};

const styles = makeStyles(() => ({
  dndDropBox: {
    alignItems: 'center',
    background: '#FBFBFB',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  invisible: {
    visibility: 'hidden',
  },
  dragAndDropImg: {
    display: 'block',
    margin: 'auto',
  },
  feedbackImg: {
    left: `calc(50% - 120px)`,
    position: 'absolute',
    top: remCalc(216),
  },
  sceneContainer: {
    visibility: 'visible',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    '& > img': {
      height: '100%',
      width: '100%',
    },
  },
  tileBackground: {
    backgroundSize: 'cover',
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default GWhoDndRound;
